import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  HostListener,
  inject,
  input,
  output,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ShButtonComponent, ShTooltipDirective } from '@seahorse/components';
import { ActionMenuItem } from '@seahorse/ui';
import { ActionBarComponent } from '../action-bar/action-bar.component';
import { PageAction } from '../page-header/page-header.models';
import { PeekStat } from './peek-panel.models';

// Elements whose own editing keeps Esc from reaching the panel (design
// handoff, "Step 6, streamlined", section 3: "ignored while focus is in an
// input, select, textarea or editable field, or inside an open overlay").
const EDITABLE_SELECTOR = 'input, select, textarea, [contenteditable]';

// PrimeNG overlay panels: while one of these is open (a select's option
// list, a menu, a popover, a datepicker, ...), its own keyboard handling
// owns the keys instead of the peek panel.
const OVERLAY_SELECTOR =
  '.p-select-overlay, .p-menu-overlay, .p-popover, .p-datepicker-panel, .p-overlay, .p-dialog';

// Modal dialogs (e.g. a "discard changes?" confirm) own the keyboard while
// open, wherever focus is: otherwise Esc dismissing the dialog would also
// reach the peek and ask again.
const OPEN_DIALOG_SELECTOR = '.p-dialog-mask';

/** True when the keydown target is inside an editable control. */
export function isEditableTarget(target: EventTarget | null): boolean {
  return target instanceof Element
    ? !!target.closest(EDITABLE_SELECTOR)
    : false;
}

/** True when the keydown target is inside an open PrimeNG overlay. */
export function isInsideOverlay(target: EventTarget | null): boolean {
  return target instanceof Element ? !!target.closest(OVERLAY_SELECTOR) : false;
}

/** True when a keyboard shortcut modifier is held (design handoff: plain keys only). */
export function hasModifier(event: KeyboardEvent): boolean {
  return event.ctrlKey || event.metaKey || event.altKey || event.shiftKey;
}

/**
 * The right-side peek panel (design handoff, "Step 6, streamlined: the peek
 * panel"): a slot-based panel the page fills with its own content. The panel
 * never knows the entity -- it only lays out the header, an optional tabs
 * slot, an optional stats strip, the scrolling body and an optional footer.
 *
 * Layout (push vs. overlay, `.peek-layout`/`.peek-panel`) lives in
 * `@seahorse/theme`'s `_peek.scss`; this component only renders its own
 * content and the keyboard shortcuts.
 */
@Component({
  selector: 'sh-peek-panel',
  // Default, not OnPush: the footer's `sh-action-bar` evaluates action
  // `visible`/`disabled` functions over page state; an OnPush host with
  // unchanged inputs would skip that subtree and leave them stale.
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    TranslateModule,
    ShButtonComponent,
    ShTooltipDirective,
    ActionBarComponent,
  ],
  templateUrl: './peek-panel.component.html',
})
export class PeekPanelComponent {
  private readonly _document = inject(DOCUMENT);

  open = input(false);
  title = input('');
  subline = input<string>();
  stats = input<PeekStat[]>();
  expandable = input(false);

  primaryAction = input<PageAction>();
  secondaryActions = input<PageAction[]>();
  overflowActions = input<ActionMenuItem[]>();

  keyboardHint = input(true);

  // eslint-disable-next-line @angular-eslint/no-output-native -- public output name; renaming would change the API
  readonly close = output<void>();
  readonly expand = output<void>();

  // The host element itself is the panel box (design handoff class list:
  // `.peek-panel`) -- see @seahorse/theme's `_peek.scss`. The page mounts
  // this component with `*ngIf="open"`, so there's no separate open/closed
  // state to track here; the slide-in animation plays once, on insertion.
  @HostBinding('class.peek-panel')
  protected readonly peekPanelClass = true;

  protected hasStats(): boolean {
    return (this.stats()?.length ?? 0) > 0;
  }

  // An expandable panel always has a footer: it holds the "More details"
  // button, even when the page gives no actions.
  protected hasFooter(): boolean {
    return (
      this.expandable() ||
      !!this.primaryAction() ||
      (this.secondaryActions()?.length ?? 0) > 0 ||
      (this.overflowActions()?.length ?? 0) > 0
    );
  }

  protected readonly trackByStatLabel = (_: number, stat: PeekStat) =>
    stat.label;

  // Keyboard: only Esc closes the panel (decided 2026-09-28). J/K and the
  // arrow keys to move between rows, and Enter to expand, were removed for
  // now as too big a change; they may come back later.
  @HostListener('document:keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (!this.open()) return;
    if (event.key !== 'Escape') return;
    if (hasModifier(event)) return;
    if (isEditableTarget(event.target) || isInsideOverlay(event.target)) return;
    if (this._document.querySelector(OPEN_DIALOG_SELECTOR)) return;
    this.close.emit();
  }
}
